/**
 * LOGOWANIE OFFICE 365 — PRZEZ PRZEKIEROWANIE, NIE PRZEZ WYSKAKUJĄCE OKNO.
 *
 * ZGŁOSZENIE, KTÓRE ZRODZIŁO TEN PLIK (2026-09-03): pracownicy nie mogą zalogować się
 * z telefonu. Na ekranie ląduje surowy komunikat MSAL:
 *   „hash_empty_error: Hash value cannot be processed because it is empty.
 *    Please verify that your redirectUri is not clearing the hash."
 *
 * PRZYCZYNA: `LoginPage` wołał `msalInstance.loginPopup(...)`. Odpowiedź Microsoftu wraca
 * we FRAGMENCIE adresu (`#code=…&state=…`), a wyskakujące okno musi ten fragment oddać
 * oknu, które je otworzyło. Na przeglądarkach mobilnych okno albo jest zablokowane, albo
 * otwiera się jako OSOBNA KARTA — a osobna karta nie ma z kim się porozumieć. MSAL dostaje
 * pusty fragment i rzuca dokładnie tym błędem. Na telefonie poprawną drogą jest
 * przekierowanie całej karty (`loginRedirect`) i odebranie odpowiedzi po powrocie.
 *
 * DLACZEGO PRZEKIEROWANIE ZAWSZE, A NIE „popup na komputerze, przekierowanie na telefonie":
 *  1. Popup i przekierowanie używają TEGO SAMEGO adresu powrotu (`window.location.origin`)
 *     i tego samego przepływu (kod autoryzacyjny + PKCE), więc rozgałęzienie niczego nie
 *     upraszcza po stronie Azure AD.
 *  2. Rozgałęzienie znaczy, że droga mobilna wykonuje się WYŁĄCZNIE na urządzeniach, przy
 *     których nikt z nas nie ma konsoli — czyli dokładnie tam, gdzie ten błąd przeżył
 *     niezauważony. Jedna droga = jedna droga do przetestowania.
 *  3. Popup psuje się nie tylko na telefonie: w oknie zainstalowanej aplikacji (PWA) i przy
 *     firmowej blokadzie wyskakujących okien na komputerze zachowuje się tak samo źle.
 *  4. Ekran logowania nie ma żadnego stanu do stracenia przy przeładowaniu strony —
 *     jedyny koszt przekierowania nas nie dotyczy.
 *
 * ⚠️ WARUNEK PO STRONIE AZURE AD (do potwierdzenia — opisany w „doDecyzji"): adres powrotu
 * musi być zarejestrowany jako platforma **SPA** (Single-page application). Popup w MSAL v4
 * wymaga DOKŁADNIE tej samej rejestracji, więc skoro logowanie z komputera działa dziś,
 * rejestracja jest już na miejscu — ale to trzeba zobaczyć w portalu, nie założyć.
 */
import { loginRequest } from "../authConfig";
import { API_URL } from "../api/apiService";

/*
 * Biblioteka MSAL wczytywana DOPIERO przy logowaniu (import dynamiczny).
 * Ten moduł jest wołany z `index.js` przy KAŻDYM starcie aplikacji (żeby złapać fragment
 * adresu przed routerem), a samo złapanie fragmentu to kilka linii — statyczny import
 * wciągnąłby całą bibliotekę Microsoftu do głównej paczki i wydłużył pierwsze wejście
 * WSZYSTKIM, także tym 500 osobom, które są już zalogowane.
 */
const wczytajMsal = async () => (await import("@azure/msal-browser")).PublicClientApplication;

/** Ślad „wyszliśmy na stronę Microsoftu" — pozwala rozpoznać powrót BEZ odpowiedzi. */
const KLUCZ_OCZEKIWANIA = "o365_przekierowanie_w_toku";

/**
 * Klucz, pod którym MSAL trzyma informację „interakcja w toku".
 * Nazwa jest stała w bibliotece (`msal.interaction.status`) i musimy ją znać, bo MSAL nie
 * wystawia publicznej metody do sprzątnięcia śladu po logowaniu, które nigdy nie wróciło.
 */
const KLUCZ_INTERAKCJI_MSAL = "msal.interaction.status";

/**
 * Fragment adresu z odpowiedzią Microsoftu, złapany PRZED uruchomieniem routera.
 * Trzymany w zmiennej modułu, nie w pamięci przeglądarki — patrz `przechwycOdpowiedzO365`.
 */
let fragmentOdpowiedzi = null;

const KOMUNIKAT_DOMYSLNY =
    "Nie udało się zalogować przez Office 365. Spróbuj ponownie, a jeśli błąd wróci — " +
    "zaloguj się adresem e-mail i hasłem.";

/**
 * Komunikaty PO POLSKU dla kodów błędów MSAL.
 *
 * BYŁO ŹLE: na ekran szedł `err.message` prosto z biblioteki — angielskie zdanie z odnośnikiem
 * do dokumentacji Microsoftu („Please verify that your redirectUri…"). Pracownik hali dostawał
 * instrukcję dla programisty i nie wiedział, że wystarczy zalogować się hasłem.
 * Szczegół techniczny nie ginie — ląduje w konsoli przeglądarki (patrz LoginPage).
 */
const KOMUNIKATY = {
    hash_empty_error:
        "Microsoft nie odesłał odpowiedzi na logowanie. Spróbuj ponownie w tej samej karcie " +
        "przeglądarki albo zaloguj się adresem e-mail i hasłem.",
    hash_does_not_contain_known_properties:
        "Odpowiedź Microsoftu wróciła niekompletna. Spróbuj ponownie albo zaloguj się adresem " +
        "e-mail i hasłem.",
    no_token_request_cache_error:
        "Przeglądarka nie zachowała danych rozpoczętego logowania — mogła je skasować, jeśli " +
        "strona Microsoftu otworzyła się w innej karcie. Spróbuj ponownie w tej samej karcie " +
        "albo zaloguj się adresem e-mail i hasłem.",
    state_not_found:
        "Sesja logowania wygasła po drodze. Kliknij „Zaloguj przez Office 365” jeszcze raz.",
    state_mismatch:
        "Sesja logowania wygasła po drodze. Kliknij „Zaloguj przez Office 365” jeszcze raz.",
    interaction_in_progress:
        "Poprzednia próba logowania jeszcze się nie zakończyła. Odśwież stronę i spróbuj ponownie.",
    user_cancelled: "Logowanie przez Microsoft zostało przerwane.",
    access_denied:
        "Microsoft nie zgodził się na zalogowanie tym kontem do mPracownika. Zgłoś to do działu IT.",
    post_request_failed:
        "Brak połączenia ze stroną logowania Microsoftu. Sprawdź sieć i spróbuj ponownie albo " +
        "zaloguj się adresem e-mail i hasłem.",
};

/** Kod błędu MSAL → zdanie po polsku. Nieznany kod = zdanie domyślne, nigdy tekst z biblioteki. */
export const komunikatO365 = (err) => {
    const kod = err && (err.errorCode || err.code);
    if (kod && KOMUNIKATY[kod]) return KOMUNIKATY[kod];
    // Awaria pobrania konfiguracji z NASZEGO backendu ma własny, już polski komunikat.
    if (err && err.komunikatWlasny) return err.komunikatWlasny;
    return KOMUNIKAT_DOMYSLNY;
};

/**
 * Czy fragment adresu to odpowiedź Microsoftu?
 *
 * MSAL odsyła ZAWSZE `state`, a obok niego `code` (zgoda) albo `error` (odmowa). Sam `state`
 * to za mało: nie chcemy porwać zwykłej kotwicy w adresie i wyciąć jej z paska adresu.
 */
const czyFragmentOdpowiedzi = (hash) => {
    if (typeof hash !== "string" || hash.length < 2) return false;
    const tresc = hash.startsWith("#") ? hash.slice(1) : hash;
    const ma = (nazwa) => new RegExp(`(^|&)${nazwa}=`).test(tresc);
    return ma("state") && (ma("code") || ma("error"));
};

/**
 * ŁAPIE ODPOWIEDŹ MICROSOFTU, ZANIM ROUTER ZDĄŻY JĄ WYRZUCIĆ. Wołane z `index.js`,
 * PRZED pierwszym renderem aplikacji.
 *
 * DLACZEGO TO MUSI BYĆ TAK WCZEŚNIE: adresem powrotu jest `window.location.origin`, czyli „/".
 * Trasa „/" to `HomeRedirect`, który przy braku zalogowania renderuje
 * `<Navigate to="/logowanie" replace />`. Cel przekierowania nie ma fragmentu, więc React
 * Router zamienia adres BEZ `#code=…` — odpowiedź Microsoftu przepada, zanim ekran logowania
 * zdąży się zamontować. Dlatego fragment zdejmujemy z adresu sami, jeszcze przed routerem,
 * i podajemy go MSAL-owi wprost (`handleRedirectPromise(hash)`).
 *
 * Przy okazji fragment znika z paska adresu i z historii przeglądarki — a niesie jednorazowy
 * kod autoryzacyjny, który nie ma czego szukać w zakładkach.
 *
 * Trzymamy go w ZMIENNEJ MODUŁU, nie w sessionStorage: przechwycenie i odczyt dzieją się
 * w tym samym załadowaniu strony (przejście na ekran logowania robi router, bez przeładowania),
 * a wartość w pamięci przeglądarki umiałaby przeżyć do następnej wizyty i wywołać wtedy
 * komunikat o błędzie logowania, którego nikt nie zaczynał.
 *
 * @returns {boolean} czy odpowiedź została przechwycona
 */
export const przechwycOdpowiedzO365 = () => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash || "";
    if (!czyFragmentOdpowiedzi(hash)) return false;

    fragmentOdpowiedzi = hash;
    try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {
        // Przeglądarka bez `replaceState` — fragment zostanie w adresie, ale logowanie zadziała.
    }
    return true;
};

/** Czy jest odpowiedź czekająca na dokończenie logowania. */
export const czekaOdpowiedzO365 = () => fragmentOdpowiedzi !== null;

const zapamietajOczekiwanie = () => {
    try { sessionStorage.setItem(KLUCZ_OCZEKIWANIA, "1"); } catch { /* tryb prywatny */ }
};

const zapomnijOczekiwanie = () => {
    try { sessionStorage.removeItem(KLUCZ_OCZEKIWANIA); } catch { /* tryb prywatny */ }
};

/**
 * Czy wróciliśmy ze strony Microsoftu BEZ odpowiedzi (cofnięcie, zamknięta karta logowania).
 * Odczyt kasuje ślad, więc komunikat pokaże się raz — a nie przy każdym wejściu na logowanie.
 */
export const bylPrzerwanyPowrotO365 = () => {
    let byl = false;
    try { byl = sessionStorage.getItem(KLUCZ_OCZEKIWANIA) === "1"; } catch { /* tryb prywatny */ }
    zapomnijOczekiwanie();
    return byl;
};

/**
 * Zdejmuje ślad po logowaniu, które nigdy nie wróciło.
 *
 * PUŁAPKA, KTÓRĄ TO ZAMYKA: gdy ktoś kliknie „Zaloguj przez Office 365" i zamknie kartę na
 * stronie Microsoftu, w pamięci przeglądarki zostaje `msal.interaction.status`. Przy kolejnej
 * próbie MSAL rzuca `interaction_in_progress` i ODMAWIA startu — przycisk logowania jest wtedy
 * martwy aż do wyczyszczenia danych strony, czego pracownik nie zrobi. Kliknięcie przycisku
 * jest jednoznaczną decyzją „zaczynamy od nowa", więc ślad usuwamy.
 *
 * Czyścimy w OBU miejscach, bo `storeAuthStateInCookie` (patrz `zbudujInstancje`) zapisuje
 * ten sam klucz także w ciasteczku.
 */
const zdejmijSladPoprzedniejProby = () => {
    try { sessionStorage.removeItem(KLUCZ_INTERAKCJI_MSAL); } catch { /* tryb prywatny */ }
    try {
        document.cookie = `${KLUCZ_INTERAKCJI_MSAL}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } catch { /* ciasteczka wyłączone */ }
};

/*
 * [S-2, polecenie właściciela 2026-09-09] SZEŚĆ SPÓŁEK = SZEŚĆ TENANTÓW. Backend zwraca listę tenantów (spółka, domeny e-mail,
 * ClientId/TenantId); ekran logowania pokazuje wybór spółki, a wybrany tenant zapamiętujemy w sessionStorage — po powrocie
 * z Microsoftu MSAL musi zbudować instancję z TĄ SAMĄ aplikacją i autorytetem, inaczej odpowiedź nie pasuje do żądania.
 */
const KLUCZ_TENANTA = "o365.tenant";

export const wybierzTenant = (tenantId) => {
    try {
        if (tenantId) window.sessionStorage.setItem(KLUCZ_TENANTA, tenantId);
        else window.sessionStorage.removeItem(KLUCZ_TENANTA);
    } catch { /* prywatny tryb — trudno, weźmiemy pierwszy tenant */ }
};

export const wybranyTenant = () => {
    try { return window.sessionStorage.getItem(KLUCZ_TENANTA) || null; } catch { return null; }
};

/** Tenant dopasowany do adresu e-mail (po domenie) — null, gdy nie ma pewności. */
export const tenantDlaAdresu = (tenanty, email) => {
    const domena = String(email || "").split("@")[1]?.trim().toLowerCase();
    if (!domena || !Array.isArray(tenanty)) return null;
    return tenanty.find((t) => Array.isArray(t.domains) && t.domains.some((d) => String(d).toLowerCase() === domena)) || null;
};

/** Wybór tenanta z listy: zapamiętany (jeśli jest na liście) → jedyny → pierwszy (deterministyczny z backendu). */
export const dobierzTenant = (konfiguracja) => {
    const lista = Array.isArray(konfiguracja?.tenants) ? konfiguracja.tenants : [];
    if (lista.length === 0) return { clientId: konfiguracja?.clientId, tenantId: konfiguracja?.tenantId };
    const zapamietany = wybranyTenant();
    return lista.find((t) => t.tenantId === zapamietany) || lista[0];
};

/** Lista tenantów do ekranu logowania (pusta przy błędzie — ekran pokazuje wtedy jeden przycisk jak dotąd). */
export const pobierzTenanty = async () => {
    try {
        const k = await pobierzKonfiguracje();
        return Array.isArray(k?.tenants) ? k.tenants : [];
    } catch {
        return [];
    }
};

/** Konfiguracja aplikacji Azure AD z naszego backendu (clientId + tenantId + lista tenantów spółek). */
const pobierzKonfiguracje = async () => {
    let odpowiedz;
    try {
        // Surowy fetch omija interceptor axios — sami szturchamy down-detektor, żeby padnięty
        // backend na ekranie logowania też włączał ekran przerwy, a nie tylko rzucał błędem.
        odpowiedz = await fetch(`${API_URL}/users/o365-config`);
    } catch (err) {
        window.dispatchEvent(new Event("backend-network-error"));
        err.komunikatWlasny = "Brak połączenia z serwerem mPracownika. Spróbuj ponownie za chwilę.";
        throw err;
    }
    if (!odpowiedz.ok) {
        if ([502, 503, 504].includes(odpowiedz.status)) {
            window.dispatchEvent(new Event("backend-network-error"));
        }
        const err = new Error("Nie można pobrać konfiguracji O365 z serwera.");
        err.komunikatWlasny = "Nie można pobrać konfiguracji Office 365 z serwera. Zgłoś to do działu IT.";
        throw err;
    }
    return odpowiedz.json();
};

const zbudujInstancje = async () => {
    // [S-2] Tenant SPÓŁKI wybranej na ekranie logowania (albo jedyny / pierwszy) — nie „pierwszy z bazy”.
    const { clientId, tenantId } = dobierzTenant(await pobierzKonfiguracje());
    const KlasaMsal = await wczytajMsal();

    const instancja = new KlasaMsal({
        auth: {
            clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: window.location.origin,
            /*
             * FAŁSZ CELOWO: przy `true` MSAL po wylądowaniu na adresie powrotu przenosi
             * przeglądarkę jeszcze raz — na adres, z którego logowanie ruszyło. To dodatkowe
             * przeładowanie nie ma tu czego uratować (fragment odbieramy sami, patrz
             * `przechwycOdpowiedzO365`), a dokładałoby kolejny moment, w którym coś może
             * zgubić odpowiedź.
             */
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: "sessionStorage",
            /*
             * DRUGA POŁOWA BŁĘDU Z TELEFONU. Dane rozpoczętego logowania (stan, jednorazowy
             * weryfikator PKCE, znacznik „interakcja w toku") MSAL trzyma domyślnie
             * w `sessionStorage`, a ta pamięć jest PER KARTA. Przekierowanie w tej samej
             * karcie ją przeżywa, ale otwarcie strony Microsoftu w NOWEJ karcie (co robią
             * blokady okien, skanery odnośników i część przeglądarek w aplikacjach) —
             * już nie: wracamy z poprawną odpowiedzią, do której nie ma klucza.
             * `storeAuthStateInCookie` odkłada te same wartości w ciasteczku, wspólnym dla
             * wszystkich kart tej domeny, więc logowanie da się dokończyć także wtedy.
             * Ciasteczko żyje tylko do zamknięcia przeglądarki i nie zawiera tokenów.
             */
            storeAuthStateInCookie: true,
        },
    });
    await instancja.initialize();
    return instancja;
};

/**
 * Startuje logowanie: przeglądarka opuszcza stronę i wraca z odpowiedzią Microsoftu.
 * Po udanym wywołaniu ten kod się już nie wykonuje — strona jest opuszczana.
 */
export const rozpocznijLogowanieO365 = async () => {
    const instancja = await zbudujInstancje();
    zdejmijSladPoprzedniejProby();
    zapamietajOczekiwanie();
    await instancja.loginRedirect(loginRequest);
};

/**
 * Dokańcza logowanie po powrocie: zamienia przechwycony fragment na token tożsamości.
 *
 * @returns {Promise<{stan:"ok", idToken:string} | {stan:"brak"} | {stan:"blad", komunikat:string, szczegol:*}>}
 */
export const dokonczLogowanieO365 = async () => {
    const fragment = fragmentOdpowiedzi;
    fragmentOdpowiedzi = null;
    zapomnijOczekiwanie();
    if (!fragment) return { stan: "brak" };

    try {
        const instancja = await zbudujInstancje();
        const wynik = await instancja.handleRedirectPromise(fragment);
        const idToken = wynik && wynik.idToken;
        if (!idToken) {
            return {
                stan: "blad",
                komunikat: KOMUNIKAT_DOMYSLNY,
                szczegol: "handleRedirectPromise nie zwrócił tokenu tożsamości",
            };
        }
        return { stan: "ok", idToken };
    } catch (err) {
        return { stan: "blad", komunikat: komunikatO365(err), szczegol: err };
    }
};
